import { useCallback, useEffect } from "react";

import { useAppBaseContext } from "providers/AppBaseProvider";
import { useProjectDataStore } from "stores/projectStore";
import {
  NodeUpdateManyMut,
  NodeCreateManyMut,
  NodeDeleteManyMut,
  WizardDeleteMut,
  ProcessNodesMut,
} from "api/mutations";
import { toast } from "react-toastify";
import type { UpdateNode } from "interfaces/schemas";
import { useRequestQueueStore } from "stores/requestQueueStore";
import { useFetchProject } from "./useFetchProject";

type Action = {
  action: "CREATE" | "DELETE" | "UPDATE";
  nodes: Partial<Node>[];
};

export const useNodeMutations = () => {
  const { lang } = useAppBaseContext() as { lang: string };
  const projectId = useProjectDataStore((state) => state.projectId);
  const setOnError = useRequestQueueStore((state) => state.setOnError);
  const enqueue = useRequestQueueStore((state) => state.enqueue);
  const refetch = useFetchProject();

  useEffect(() => {
    if (refetch) {
      setOnError(() => {
        refetch();
        toast.error("Error updating project");
      });
    }
  }, [refetch, setOnError]);

  const updateNodes = useCallback(
    (nodes: UpdateNode[]) =>
      enqueue({
        query: NodeUpdateManyMut,
        variables: {
          input: {
            projectId,
            languageCode: lang,
            nodes,
          },
        },
      }),
    [enqueue, lang, projectId],
  );

  const createNodes = useCallback(
    (nodes: Node[]) =>
      enqueue({
        query: NodeCreateManyMut,
        variables: {
          input: { projectId, languageCode: lang, nodes },
        },
      }),
    [enqueue, lang, projectId],
  );

  const deleteNodes = useCallback(
    (ids: string[]) =>
      enqueue({
        query: NodeDeleteManyMut,
        variables: {
          input: { projectId, languageCode: lang, ids },
        },
      }),
    [enqueue, projectId, lang],
  );

  const deleteWizard = useCallback(
    (nodeId: string) =>
      enqueue({
        query: WizardDeleteMut,
        variables: {
          input: { projectId, languageCode: lang, nodeId },
        },
      }),
    [enqueue, projectId, lang],
  );

  const processNodes = useCallback(
    (actions: Action[]) =>
      enqueue({
        query: ProcessNodesMut,
        variables: {
          input: {
            projectId,
            languageCode: lang,
            actions,
          },
        },
      }),
    [enqueue, projectId, lang],
  );

  return {
    updateNodes,
    createNodes,
    deleteNodes,
    deleteWizard,
    processNodes,
  };
};
